import React, { useState, useEffect } from 'react';
// Numeric formatter only; labels in this screen say "tokens" for merchants/admins.
import { formatCredits } from '../../utils/numberFormat';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  InputAdornment,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search,
  Download,
  Visibility,
  Edit,
  Delete,
  Add,
  Refresh,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Cancel,
  Schedule,
  AttachMoney,
  Receipt,
  History,
} from '@mui/icons-material';
import { creditSystemAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { masterDataConfigAPI } from '../../services/api';


// CreditSystemManagement.js — UI copy uses "tokens"; APIs and payloads still use credit* field names.
// Manages packages, transactions, balance requests, and subscription requests.
// Transaction search supports exact numeric amount match plus description/reference text.
//
// Stats display:
// - Total tokens issued: sum of CREDIT-type transactions (API: total_credits_issued)
// - Total revenue: from approved balance/subscription flows
// - Pending requests: PENDING count
// - Active packages: is_active and not soft-deleted (status=1)
//
// Auto-refresh: Stats automatically refresh after any package CRUD operation to ensure accurate counts
const getUserIdentity = (user, isConfig = false) => {
  if (!user) return 'N/A';
  if (isConfig) {
    if (user.user_email) return user.user_email;
    if (user.user_whatsapp) return user.user_whatsapp;
    if (user.user_mobile) return user.user_mobile;
  } else {
    if (user.email) return user.email;
    if (user.whatsapp_number) return user.whatsapp_number;
    if (user.mobile) return user.mobile;
  }
  return 'N/A';
};

const getUserIdentityLabel = (user, isConfig = false) => {
  if (!user) return '';
  const name = isConfig ? user.user_name : user.name;
  const identity = getUserIdentity(user, isConfig);
  return `${name || 'Unknown User'} (${identity})`;
};

const CreditSystemManagement = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('CreditSystemManagement', 'view');
  const canCreate = hasPermission('CreditSystemManagement', 'create');
  const canUpdate = hasPermission('CreditSystemManagement', 'update');
  const canDelete = hasPermission('CreditSystemManagement', 'delete');
  const canExport = hasPermission('CreditSystemManagement', 'export');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  
  // Packages state
  const [packages, setPackages] = useState([]);
  const [packagePage, setPackagePage] = useState(0);
  const [packageRowsPerPage, setPackageRowsPerPage] = useState(10);
  const [packageTotal, setPackageTotal] = useState(0);
  const [packageDialog, setPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    credits: '',
    price: '',
    description: '',
    is_active: true
  });
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [transactionMerchantFilter, setTransactionMerchantFilter] = useState('');
  const [transactionFromDate, setTransactionFromDate] = useState('');
  const [transactionToDate, setTransactionToDate] = useState('');
  const [transactionDateError, setTransactionDateError] = useState('');
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [merchants, setMerchants] = useState([]);
  
  // Credit Requests state
  const [creditRequests, setCreditRequests] = useState([]);
  const [requestPage, setRequestPage] = useState(0);
  const [requestRowsPerPage, setRequestRowsPerPage] = useState(10);
  const [requestTotal, setRequestTotal] = useState(0);
  const [requestDialog, setRequestDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestAction, setRequestAction] = useState('');
  
  // Subscription Requests state
  const [subscriptionRequests, setSubscriptionRequests] = useState([]);
  const [subscriptionRequestPage, setSubscriptionRequestPage] = useState(0);
  const [subscriptionRequestRowsPerPage, setSubscriptionRequestRowsPerPage] = useState(10);
  const [subscriptionRequestTotal, setSubscriptionRequestTotal] = useState(0);
  const [subscriptionRequestDialog, setSubscriptionRequestDialog] = useState(false);
  const [selectedSubscriptionRequest, setSelectedSubscriptionRequest] = useState(null);
  const [subscriptionRequestAction, setSubscriptionRequestAction] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [requestMerchantFilter, setRequestMerchantFilter] = useState('');
  const [requestFromDate, setRequestFromDate] = useState('');
  const [requestToDate, setRequestToDate] = useState('');
  const [requestDateError, setRequestDateError] = useState('');
  
  // Subscription request specific date states
  const [subscriptionRequestFromDate, setSubscriptionRequestFromDate] = useState('');
  const [subscriptionRequestToDate, setSubscriptionRequestToDate] = useState('');
  const [subscriptionRequestDateError, setSubscriptionRequestDateError] = useState('');
  const [requestStatuses, setRequestStatuses] = useState([]);
  const [maxCreditLimit, setMaxCreditLimit] = useState(10000000); // Default fallback for MAX_CREDIT_ALLOCATION_LIMIT
  const [requestSearchLoading, setRequestSearchLoading] = useState(false); // Loading state for search button

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);


  useEffect(() => {
    loadStats();
    loadPackages();
    loadTransactions();
    // Load credit requests without filters initially
    loadCreditRequests();
    loadTransactionTypes();
    loadMerchants();
    loadRequestStatuses();
    loadSystemSettings(); // Load system settings for credit limits
  }, [packagePage, packageRowsPerPage, transactionPage, transactionRowsPerPage, requestPage, requestRowsPerPage]);

  // Load subscription requests when tab is active (tab index 3)
  useEffect(() => {
    if (activeTab === 3) {
      loadSubscriptionRequests();
    }
  }, [activeTab, subscriptionRequestPage, subscriptionRequestRowsPerPage]);

  const loadStats = async () => {
    try {
      const response = await creditSystemAPI.getStats();
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadPackages = async () => {
    try {
      const params = {
        page: packagePage + 1,
        per_page: packageRowsPerPage
      };
      const response = await creditSystemAPI.getPackages(params);
      setPackages(response.data.packages || []);
      setPackageTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load packages:', error);
      setError('Failed to load token packages');
    }
  };

  const loadTransactions = async () => {
    try {
      const params = {
        page: transactionPage + 1,
        per_page: transactionRowsPerPage,
        search: transactionSearch,
        type: transactionTypeFilter,
        merchant_id: transactionMerchantFilter,
        start_date: transactionFromDate,
        end_date: transactionToDate
      };
      const response = await creditSystemAPI.getTransactions(params);
      setTransactions(response.data.transactions || []);
      setTransactionTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setError('Failed to load transactions');
    }
  };

  // Clear all transaction filters and reload
  const clearTransactionFilters = async () => {
    // Clear all filter states
    setTransactionSearch('');
    setTransactionTypeFilter('');
    setTransactionMerchantFilter('');
    setTransactionFromDate('');
    setTransactionToDate('');
    setTransactionDateError('');
    setTransactionPage(0);
    
    // Load transactions with cleared filters
    try {
      const params = {
        page: 1,
        per_page: transactionRowsPerPage,
        search: '',
        type: '',
        merchant_id: '',
        start_date: '',
        end_date: ''
      };
      const response = await creditSystemAPI.getTransactions(params);
      setTransactions(response.data.transactions || []);
      setTransactionTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to clear transaction filters:', error);
      setError('Failed to clear transaction filters');
    }
  };

  const loadCreditRequests = async () => {
    try {
      const params = {
        page: requestPage + 1,
        per_page: requestRowsPerPage,
        status: requestStatusFilter,
        merchant_id: requestMerchantFilter,
        start_date: requestFromDate,
        end_date: requestToDate
      };
      const response = await creditSystemAPI.getCreditRequests(params);
      console.log('Credit requests response:', response.data); // Debug log
      setCreditRequests(response.data.requests || []);
      setRequestTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load credit requests:', error);
      setError('Failed to load token balance requests');
    }
  };

  const loadSubscriptionRequests = async () => {
    try {
      const params = {
        page: subscriptionRequestPage + 1,
        per_page: subscriptionRequestRowsPerPage,
        status: requestStatusFilter,
        merchant_id: requestMerchantFilter,
        start_date: subscriptionRequestFromDate,
        end_date: subscriptionRequestToDate
      };
      const response = await creditSystemAPI.getSubscriptionRequests(params);
      console.log('Subscription requests response:', response.data); // Debug log
      setSubscriptionRequests(response.data.requests || []);
      setSubscriptionRequestTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load subscription requests:', error);
      setError('Failed to load subscription requests');
    }
  };

  // Search credit requests with current filters - called when search button is clicked or pagination changes
  const searchCreditRequests = async () => {
    setRequestSearchLoading(true);
    try {
      const params = {
        page: requestPage + 1, // Use current page
        per_page: requestRowsPerPage,
        status: requestStatusFilter,
        merchant_id: requestMerchantFilter,
        start_date: requestFromDate,
        end_date: requestToDate
      };
      const response = await creditSystemAPI.getCreditRequests(params);
      console.log('Credit requests search response:', response.data); // Debug log
      setCreditRequests(response.data.requests || []);
      setRequestTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to search credit requests:', error);
      setError('Failed to search token balance requests');
    } finally {
      setRequestSearchLoading(false);
    }
  };

  // Clear all credit request filters and reload
  const clearCreditRequestFilters = async () => {
    // Clear all filter states
    setRequestStatusFilter('');
    setRequestMerchantFilter('');
    setRequestFromDate('');
    setRequestToDate('');
    setRequestDateError('');
    setRequestPage(0);
    
    // Load credit requests with cleared filters
    try {
      const params = {
        page: 1,
        per_page: requestRowsPerPage,
        status: '',
        merchant_id: '',
        start_date: '',
        end_date: ''
      };
      const response = await creditSystemAPI.getCreditRequests(params);
      setCreditRequests(response.data.requests || []);
      setRequestTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to clear credit request filters:', error);
      setError('Failed to clear token balance request filters');
    }
  };

  // Clear all subscription request filters and reload
  const clearSubscriptionRequestFilters = async () => {
    // Clear all filter states for subscription requests
    setRequestStatusFilter('');
    setRequestMerchantFilter('');
    setSubscriptionRequestFromDate('');
    setSubscriptionRequestToDate('');
    setSubscriptionRequestDateError('');
    setSubscriptionRequestPage(0);
    
    // Load subscription requests with cleared filters
    try {
      const params = {
        page: 1,
        per_page: subscriptionRequestRowsPerPage,
        status: '',
        merchant_id: '',
        start_date: '',
        end_date: ''
      };
      const response = await creditSystemAPI.getSubscriptionRequests(params);
      setSubscriptionRequests(response.data.requests || []);
      setSubscriptionRequestTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to clear subscription request filters:', error);
      setError('Failed to clear subscription request filters');
    }
  };

  const loadTransactionTypes = async () => {
    try {
      const response = await creditSystemAPI.getTransactionTypes();
      setTransactionTypes(response.data.transaction_types || []);
    } catch (error) {
      console.error('Failed to load transaction types:', error);
      // Keep default empty array if loading fails
    }
  };

  // Load merchants with "All Merchants" option
  const loadMerchants = async () => {
    try {
      const response = await creditSystemAPI.getMerchants();
      const merchantsData = response.data.merchants || [];
      // Add "All Merchants" option at the beginning
      setMerchants([
        { value: "", label: "All Merchants" },
        ...merchantsData
      ]);
      // The Autocomplete component provides search functionality for merchants:
      // 1. Users can type to search through merchant names
      // 2. Search is case-insensitive and matches anywhere in the merchant name
      // 3. The filterOptions prop customizes how search matching works
      // 4. Clear buttons allow users to reset their selection
    } catch (error) {
      console.error('Failed to load merchants:', error);
      // Keep default empty array if loading fails
    }
  };

  const loadRequestStatuses = async () => {
    try {
      const response = await creditSystemAPI.getCreditRequestStatuses();
      setRequestStatuses(response.data.statuses || []);
    } catch (error) {
      console.error('Failed to load request statuses:', error);
      // Keep default empty array if loading fails
    }
  };

  // Load system settings for credit limits on component mount
  // This fetches the MAX_CREDIT_ALLOCATION_LIMIT from the backend for validation
  const loadSystemSettings = async () => {
    try {
      const response = await masterDataConfigAPI.getPublicSystemSettings();
      const settings = response.data.settings;
      if (settings.MAX_CREDIT_ALLOCATION_LIMIT) {
        setMaxCreditLimit(settings.MAX_CREDIT_ALLOCATION_LIMIT);
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
      // Keep default value if fetch fails
    }
  };

  const handleCreatePackage = async () => {
    if (!canCreate) {
      setError('You do not have permission to create packages');
      return;
    }
    
    // Validate credit amount before creating package
    const validationError = validateCreditAmount(packageForm.credits);
    if (validationError) {
      setError(`Cannot create package: ${validationError}`);
      return;
    }
    
    try {
      await creditSystemAPI.createPackage(packageForm);
      setPackageDialog(false);
      setPackageForm({ name: '', credits: '', price: '', description: '', is_active: true });
      setError(''); // Clear any previous errors
      
      // Navigate to first page to show new package
      setPackagePage(0);
      loadPackages();
      loadStats(); // Refresh stats to show updated active packages count
    } catch (error) {
      console.error('Failed to create package:', error);
      // Extract actual error message from server response
      const errorMessage = error.response?.data?.error || 'Failed to create package';
      setError(errorMessage);
      // Keep dialog open to show error message inside
    }
  };

  const handleUpdatePackage = async () => {
    if (!canUpdate) {
      setError('You do not have permission to update packages');
      return;
    }
    
    // Validate credit amount before updating package
    const validationError = validateCreditAmount(packageForm.credits);
    if (validationError) {
      setError(`Cannot update package: ${validationError}`);
      return;
    }
    
    try {
      await creditSystemAPI.updatePackage(editingPackage.id, packageForm);
      setPackageDialog(false);
      setEditingPackage(null);
      setPackageForm({ name: '', credits: '', price: '', description: '', is_active: true });
      setError(''); // Clear any previous errors
      
      // Reload current page to show updated package
      loadPackages();
      loadStats(); // Refresh stats to show updated active packages count
    } catch (error) {
      console.error('Failed to update package:', error);
      // Extract actual error message from server response
      const errorMessage = error.response?.data?.error || 'Failed to update package';
      setError(errorMessage);
      // Keep dialog open to show error message inside
    }
  };

  const handleDeletePackage = (pkg) => {
    setPackageToDelete(pkg);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      setError('You do not have permission to delete packages');
      return;
    }
    
    if (packageToDelete) {
      try {
        await creditSystemAPI.deletePackage(packageToDelete.id);
        setDeleteDialogOpen(false);
        setPackageToDelete(null);
        
        // If current page becomes empty after deletion, go to previous page
        if (packages.length === 1 && packagePage > 0) {
          setPackagePage(packagePage - 1);
        } else {
          loadPackages();
        }
        loadStats(); // Refresh stats to show updated active packages count
      } catch (error) {
        console.error('Failed to delete package:', error);
        setError('Failed to delete package');
        setDeleteDialogOpen(false);
        setPackageToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPackageToDelete(null);
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      credits: pkg.credits.toString(),
      price: pkg.price.toString(),
      description: pkg.description || '',
      is_active: pkg.is_active
    });
    setError(''); // Clear any previous errors when opening dialog
    setPackageDialog(true);
  };

  const handleNewPackage = () => {
    setEditingPackage(null);
    setPackageForm({ name: '', credits: '', price: '', description: '', is_active: true });
    setError(''); // Clear any previous errors when opening dialog
    setPackageDialog(true);
  };

  // Validates package / request amounts against min and MAX_CREDIT_ALLOCATION_LIMIT (API field names unchanged).
  const validateCreditAmount = (amount) => {
    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < 100) {
      return 'Minimum custom token amount is 100';
    }
    if (numAmount > maxCreditLimit) {
      return `Maximum allowed is ${maxCreditLimit.toLocaleString()} tokens`;
    }
    return null;
  };

  // Validate date range for transaction and balance-request filters
  const validateDateRange = (fromDate, toDate) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      // Check if from date is greater than to date
      if (from > to) {
        return { isValid: false, message: 'From date cannot be greater than to date' };
      }
      
      // Check if from date is in the future
      if (from > today) {
        return { isValid: false, message: 'From date cannot be in the future' };
      }
      
      // Check if to date is in the future
      if (to > today) {
        return { isValid: false, message: 'To date cannot be in the future' };
      }
    } else if (fromDate) {
      const from = new Date(fromDate);
      // Check if from date is in the future
      if (from > today) {
        return { isValid: false, message: 'From date cannot be in the future' };
      }
    } else if (toDate) {
      const to = new Date(toDate);
      // Check if to date is in the future
      if (to > today) {
        return { isValid: false, message: 'To date cannot be in the future' };
      }
    }
    
    return { isValid: true, message: 'Valid date range' };
  };

  const handleRequestAction = async () => {
    if (!canUpdate) {
      setError('You do not have permission to approve or reject token balance requests');
      return;
    }
    
    // Validate credit amount before processing
    if (requestAction === 'approve' && selectedRequest) {
      const validationError = validateCreditAmount(selectedRequest.requested_credits);
      if (validationError) {
        setError(`Cannot approve request: ${validationError}`);
        return;
      }
    }
    
    try {
      if (requestAction === 'approve') {
        const payload = { admin_notes: requestReason };
        console.log('Approve payload:', payload);
        await creditSystemAPI.approveCreditRequest(selectedRequest.id, payload);
      } else {
        const payload = { rejection_reason: requestReason };
        console.log('Reject payload:', payload);
        await creditSystemAPI.rejectCreditRequest(selectedRequest.id, payload);
      }
      setRequestDialog(false);
      setSelectedRequest(null);
      setRequestAction('');
      setRequestReason('');
      loadCreditRequests();
      loadStats();
    } catch (error) {
      console.error('Failed to process request:', error);
      // Extract actual error message from server response
      const errorMessage = error.response?.data?.error || 'Failed to process request';
      setError(errorMessage);
    }
  };

  const handleSubscriptionRequestAction = (request, action) => {
    setSelectedSubscriptionRequest(request);
    setSubscriptionRequestAction(action);
    setSubscriptionRequestDialog(true);
    setRequestReason('');
  };

  const handleViewSubscriptionRequest = (request) => {
    setSelectedSubscriptionRequest(request);
    setSubscriptionRequestAction('view');
    setSubscriptionRequestDialog(true);
    setRequestReason('');
  };

  const handleSubscriptionRequestSubmit = async () => {
    if (!canUpdate) {
      setError('You do not have permission to approve or reject subscription requests');
      return;
    }
    
    try {
      if (subscriptionRequestAction === 'approve') {
        const payload = { admin_notes: requestReason };
        console.log('Approve subscription request payload:', payload);
        const response = await creditSystemAPI.approveCreditRequest(selectedSubscriptionRequest.id, payload);
        
        // Show detailed success message for subscription requests
        if (response.data.subscription) {
          const subscription = response.data.subscription;
          let successMessage = `Subscription created successfully!\n\n`;
          successMessage += `Package: ${subscription.package_name}\n`;
          successMessage += `Total tokens: ${formatCredits(subscription.total_credits)}\n`;
          successMessage += `Remaining tokens: ${formatCredits(subscription.remaining_credits)}\n`;
          
          if (subscription.carried_forward_credits > 0) {
            successMessage += `Carried forward: ${formatCredits(subscription.carried_forward_credits)} tokens\n`;
          }
          
          if (subscription.expiry_date) {
            // DD/MM/YYYY in IST (en-GB ordering) to match other admin date displays
            const expiryDate = new Date(subscription.expiry_date).toLocaleDateString('en-GB', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: 'Asia/Kolkata'
            });
            successMessage += `Expires: ${expiryDate}`;
          }
          
          // Show success message (you might want to implement a proper success dialog)
          console.log('Subscription created:', successMessage);
        }
      } else if (subscriptionRequestAction === 'reject') {
        const payload = { rejection_reason: requestReason };
        console.log('Reject subscription request payload:', payload);
        await creditSystemAPI.rejectCreditRequest(selectedSubscriptionRequest.id, payload);
      }
      setSubscriptionRequestDialog(false);
      setSelectedSubscriptionRequest(null);
      setSubscriptionRequestAction('');
      setRequestReason('');
      loadSubscriptionRequests();
      loadStats();
    } catch (error) {
      console.error('Failed to process subscription request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process subscription request';
      setError(errorMessage);
    }
  };

  const handleExportTransactions = async () => {
    if (!canExport) {
      setError('You do not have permission to export transactions');
      return;
    }
    
    try {
      const response = await creditSystemAPI.exportTransactions({
        search: transactionSearch,
        type: transactionTypeFilter,
        merchant_id: transactionMerchantFilter
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'credit-transactions-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export transactions:', error);
      setError('Failed to export transactions');
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'CREDIT': return 'success';
      case 'DEBIT': return 'error';
      case 'REFUND': return 'warning';
      default: return 'default';
    }
  };

  const getRequestStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  // Format date and time: date as DD/MM/YYYY, 12-hour time; IST for admin consistency.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // en-GB keeps day/month/year order; en-US would show MM/DD/YYYY
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (!canView) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to view Token System Management.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Token System Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => { 
              loadStats(); 
              loadPackages(); 
              loadTransactions(); 
              // Tab order: 0 packages, 1 transactions, 2 token requests, 3 subscription requests
              if (activeTab === 2) {
                // Preserve current filters on the token-requests tab (same as Search there)
                searchCreditRequests();
              } else {
                loadCreditRequests();
              }
              if (activeTab === 3) {
                loadSubscriptionRequests();
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3.5} md={3.5}>
          <Card elevation={0} sx={{ border: '2px solid #1E4966', borderRadius: '16px', background: '#fff', overflow: 'hidden' }}>
            <Box sx={{ height: '8px', backgroundColor: '#1E4966' }} />
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalance sx={{ mr: 1.5, color: '#1E4966' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{ wordBreak: 'break-word', fontSize: '2rem', fontWeight: 700 }}>
                    {stats.total_credits_issued ? formatCredits(stats.total_credits_issued) : '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Total Tokens Issued</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3.5} md={3.5}>
          <Card elevation={0} sx={{ border: '2px solid #882DC0', borderRadius: '16px', background: '#fff', overflow: 'hidden' }}>
            <Box sx={{ height: '8px', backgroundColor: '#882DC0' }} />
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp sx={{ mr: 1.5, color: '#882DC0' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{ wordBreak: 'break-word', fontSize: '2rem', fontWeight: 700 }}>
                    {formatCurrency(stats.total_revenue || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Total Revenue</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.5} md={2.5}>
          <Card elevation={0} sx={{ border: '2px solid #D9627F', borderRadius: '16px', background: '#fff', overflow: 'hidden' }}>
            <Box sx={{ height: '8px', backgroundColor: '#D9627F' }} />
            <CardContent>
              <Box display="flex" alignItems="center">
                <Schedule sx={{ mr: 1.5, color: '#D9627F' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{ wordBreak: 'break-word', fontSize: '2rem', fontWeight: 700 }}>{stats.pending_requests || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Pending Requests</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.5} md={2.5}>
          <Card elevation={0} sx={{ border: '2px solid #F5CA02', borderRadius: '16px', background: '#fff', overflow: 'hidden' }}>
            <Box sx={{ height: '8px', backgroundColor: '#F5CA02' }} />
            <CardContent>
              <Box display="flex" alignItems="center">
                <Receipt sx={{ mr: 1.5, color: '#F5CA02' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{ wordBreak: 'break-word', fontSize: '2rem', fontWeight: 700 }}>{stats.active_packages || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Active Packages</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => {
          setActiveTab(newValue);
          setError('');
        }} variant="scrollable" scrollButtons="auto">
          <Tab label="Token Packages" />
          <Tab label="Transactions" />
          <Tab label="Token Requests" />
          <Tab label="Subscription Requests" />
        </Tabs>
      </Paper>


      {/* ============================================================
          TOKEN PACKAGES TAB (index 0)
      ============================================================ */}
      {activeTab === 0 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">Token Packages</Typography>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewPackage}
              >
                New Package
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              {/* Empty state when no packages */}
              <TableBody>
                {packages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <AttachMoney sx={{ fontSize: 60, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary">
                          No token packages found
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 500 }}>
                          {canCreate ? 
                            "Click the 'New Package' button above to create your first token package." : 
                            "No token packages have been created yet."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  packages.map((pkg) => (
                    <TableRow key={pkg.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{pkg.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCredits(pkg.credits)} tokens
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(pkg.price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pkg.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.is_active ? 'Active' : 'Inactive'}
                          color={pkg.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {canUpdate && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditPackage(pkg)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeletePackage(pkg)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            '& .MuiTablePagination-root': {
              borderTop: '1px solid #e0e0e0',
              width: '100%'
            }
          }}>
            <TablePagination
              component="div"
              count={packageTotal}
              page={packagePage}
              onPageChange={(e, newPage) => setPackagePage(newPage)}
              rowsPerPage={packageRowsPerPage}
              onRowsPerPageChange={(e) => {
                setPackageRowsPerPage(parseInt(e.target.value, 10));
                setPackagePage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Paper>
      )}

      {/* Transactions Tab (index 1) */}
      {activeTab === 1 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">Token Transactions</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={clearTransactionFilters}
                sx={{ 
                  height: '40px',
                  minWidth: '120px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
                title="Click to clear all filters and show all results"
              >
                Clear Filter
              </Button>
              {canExport && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExportTransactions}
                >
                  Export
                </Button>
              )}
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
               <TextField
                 fullWidth
                 placeholder="Search by token amount, description, or reference ID..."
                 value={transactionSearch}
                 onChange={(e) => setTransactionSearch(e.target.value)}
                 InputProps={{
                   startAdornment: (
                     <InputAdornment position="start">
                       <Search />
                     </InputAdornment>
                   ),
                 }}
               />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={merchants}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={merchants.find(merchant => merchant.value === transactionMerchantFilter) || null}
                onChange={(event, newValue) => {
                  setTransactionMerchantFilter(newValue ? newValue.value : '');
                  // Don't reset page automatically - wait for search button click
                }}
                filterOptions={(options, state) => {
                  // Custom filter to match search text anywhere in the merchant name
                  const inputValue = state.inputValue.toLowerCase().trim();
                  if (!inputValue) return options;
                  
                  return options.filter(option => 
                    option.label.toLowerCase().includes(inputValue)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merchant Filter"
                    placeholder="Search merchants..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                sx={{ width: '100%' }}
                disableClearable={false}
                clearOnBlur={false}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="transaction-type-filter-label">Type Filter</InputLabel>
                <Select
                  labelId="transaction-type-filter-label"
                  value={transactionTypeFilter}
                  label="Type Filter"
                  displayEmpty
                  onChange={(e) => setTransactionTypeFilter(e.target.value)}
                >
                  {/* Empty value = no type filter; sent as omitted type param on Search via loadTransactions */}
                  <MenuItem value="">
                    <em>All Types</em>
                  </MenuItem>
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label ?? type.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={transactionFromDate}
                  onChange={(e) => {
                    setTransactionFromDate(e.target.value);
                    // Clear error when date changes
                    setTransactionDateError('');
                    // Real-time validation
                    const dateValidation = validateDateRange(e.target.value, transactionToDate);
                    if (!dateValidation.isValid) {
                      setTransactionDateError(dateValidation.message);
                    }
                  }}
                  error={!!transactionDateError}
                  helperText={transactionDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={transactionToDate}
                  onChange={(e) => {
                    setTransactionToDate(e.target.value);
                    // Clear error when date changes
                    setTransactionDateError('');
                    // Real-time validation
                    const dateValidation = validateDateRange(transactionFromDate, e.target.value);
                    if (!dateValidation.isValid) {
                      setTransactionDateError(dateValidation.message);
                    }
                  }}
                  error={!!transactionDateError}
                  helperText={transactionDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Search Button */}
          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Search />}
              disabled={!!transactionDateError}
              onClick={() => {
                // Validate date range before searching
                const dateValidation = validateDateRange(transactionFromDate, transactionToDate);
                if (!dateValidation.isValid) {
                  setTransactionDateError(dateValidation.message);
                  return;
                }
                setTransactionDateError(''); // Clear any previous errors
                setTransactionPage(0);
                loadTransactions();
              }}
              sx={{ 
                height: '40px',
                minWidth: '120px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              title="Click to apply all filters and search for results"
            >
              Search
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>I2I/I2V</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              {/* Empty state when no transactions */}
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Receipt sx={{ fontSize: 60, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary">
                          No token transactions found
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 500 }}>
                          {transactionSearch || transactionTypeFilter || transactionMerchantFilter ? 
                            "Try adjusting your search filters to see more results." : 
                            "Token transactions will appear here when merchants purchase or use tokens."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{transaction.merchant_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.merchant_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(transaction.transaction_type || '').replace('_', ' ')}
                          color={getTransactionTypeColor(transaction.transaction_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCredits(transaction.credits)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {transaction.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {transaction.is_ai_generation_related ? 
                            (transaction.generation_type || 'I2I') : 
                            (transaction.generation_type || 'N/A')}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(transaction.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            '& .MuiTablePagination-root': {
              borderTop: '1px solid #e0e0e0',
              width: '100%'
            }
          }}>
            <TablePagination
              component="div"
              count={transactionTotal}
              page={transactionPage}
              onPageChange={(e, newPage) => setTransactionPage(newPage)}
              rowsPerPage={transactionRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTransactionRowsPerPage(parseInt(e.target.value, 10));
                setTransactionPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Paper>
      )}


      {/* Token balance requests tab (index 2); API: getCreditRequests */}
      {activeTab === 2 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
            <Typography variant="h6">Token Requests</Typography>
              <Typography variant="caption" color="text.secondary">
                Maximum token allocation limit: {formatCredits(maxCreditLimit)} tokens
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={clearCreditRequestFilters}
            >
              Clear Filters
            </Button>
          </Box>

          {/* Filters for token balance requests */}
          {/* Note: Filters are applied only when the Search button is clicked to prevent automatic loading */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={merchants}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={merchants.find(merchant => merchant.value === requestMerchantFilter) || null}
                  onChange={(event, newValue) => {
                    setRequestMerchantFilter(newValue ? newValue.value : '');
                    // Don't reset page automatically - wait for search button click
                  }}
                  filterOptions={(options, state) => {
                    // Custom filter to match search text anywhere in the merchant name
                    const inputValue = state.inputValue.toLowerCase().trim();
                    if (!inputValue) return options;
                    
                    return options.filter(option => 
                      option.label.toLowerCase().includes(inputValue)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Merchant"
                      placeholder="Search merchants..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ width: '100%' }}
                  disableClearable={false}
                  clearOnBlur={false}
                  clearOnEscape
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={requestStatusFilter}
                    label="Filter by Status"
                    onChange={(e) => {
                      setRequestStatusFilter(e.target.value);
                      // Don't reset page automatically - wait for search button click
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {requestStatuses.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From Date"
                    value={requestFromDate}
                    onChange={(e) => {
                      setRequestFromDate(e.target.value);
                      // Clear error when date changes
                      setRequestDateError('');
                      // Real-time validation
                      const dateValidation = validateDateRange(e.target.value, requestToDate);
                      if (!dateValidation.isValid) {
                        setRequestDateError(dateValidation.message);
                      }
                    }}
                    error={!!requestDateError}
                    helperText={requestDateError}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0] // Disable future dates
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="To Date"
                    value={requestToDate}
                    onChange={(e) => {
                      setRequestToDate(e.target.value);
                      // Clear error when date changes
                      setRequestDateError('');
                      // Real-time validation
                      const dateValidation = validateDateRange(requestFromDate, e.target.value);
                      if (!dateValidation.isValid) {
                        setRequestDateError(dateValidation.message);
                      }
                    }}
                    error={!!requestDateError}
                    helperText={requestDateError}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0] // Disable future dates
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Search Button Row */}
          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={() => {
                // Validate date range before searching
                const dateValidation = validateDateRange(requestFromDate, requestToDate);
                if (!dateValidation.isValid) {
                  setRequestDateError(dateValidation.message);
                  return;
                }
                setRequestDateError(''); // Clear any previous errors
                setRequestPage(0); // Reset to first page when searching
                searchCreditRequests();
              }}
              disabled={requestSearchLoading || !!requestDateError}
              sx={{ 
                height: '40px', 
                minWidth: '120px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {requestSearchLoading ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Requested Tokens</TableCell>
                  <TableCell>Reason / Notes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              {/* Empty state when no balance requests */}
              <TableBody>
                {creditRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <History sx={{ fontSize: 60, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary">
                          No token requests found
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 500 }}>
                          {requestStatusFilter || requestMerchantFilter ? 
                            "Try adjusting your filters to see more results." : 
                            "Token requests will appear here when merchants request additional tokens."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  creditRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{request.merchant_name || 'Unknown'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.merchant_email || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCredits(request.requested_credits)}
                        </Typography>
                        {/* Warn when requested amount exceeds max allocation (API: requested_credits) */}
                        {request.requested_credits > maxCreditLimit && (
                          <Typography variant="caption" color="error.main" display="block">
                            ⚠️ Exceeds maximum limit ({formatCredits(maxCreditLimit)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {request.status === 'REJECTED' ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {request.rejection_reason || 'No rejection reason provided'}
                            </Typography>
                            {request.request_reason && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Original Request: {request.request_reason}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : request.status === 'APPROVED' ? (
                          <Box>
                            {request.admin_notes && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {request.admin_notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {request.request_reason || 'No reason provided'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status || 'UNKNOWN'}
                          color={getRequestStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(request.created_at)}</TableCell>
                      <TableCell>
                        {request.status === 'PENDING' && canUpdate ? (
                          <Box display="flex" gap={1}>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setRequestAction('approve');
                                  setRequestDialog(true);
                                }}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setRequestAction('reject');
                                  setRequestDialog(true);
                                }}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {request.status === 'APPROVED' ? 'Approved' : 
                             request.status === 'REJECTED' ? 'Rejected' : 
                             'No actions available'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            '& .MuiTablePagination-root': {
              borderTop: '1px solid #e0e0e0',
              width: '100%'
            }
          }}>
            <TablePagination
              component="div"
              count={requestTotal}
              page={requestPage}
              onPageChange={(e, newPage) => {
                setRequestPage(newPage);
                // Load specific page when pagination changes - use search to maintain filters
                searchCreditRequests();
              }}
              rowsPerPage={requestRowsPerPage}
              onRowsPerPageChange={(e) => {
                setRequestRowsPerPage(parseInt(e.target.value, 10));
                setRequestPage(0);
                // Load first page when rows per page changes - use search to maintain filters
                searchCreditRequests();
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Paper>
      )}

      {/* Subscription Requests Tab */}
      {/* Subscription Requests Tab (index 3) */}
      {activeTab === 3 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h6">Subscription Requests</Typography>
              <Typography variant="caption" color="text.secondary">
                Manage subscription requests from merchants
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={clearSubscriptionRequestFilters}
                sx={{ 
                  height: '40px',
                  minWidth: '120px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                title="Click to clear all filters and show all results"
              >
                Clear Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => loadSubscriptionRequests()}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Subscription Request Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={merchants}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={merchants.find(merchant => merchant.value === requestMerchantFilter) || null}
                  onChange={(event, newValue) => {
                    setRequestMerchantFilter(newValue ? newValue.value : '');
                  }}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase().trim();
                    if (!inputValue) return options;
                    return options.filter(option => 
                      option.label.toLowerCase().includes(inputValue)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Merchant"
                      placeholder="Search merchants..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {requestMerchantFilter && (
                              <IconButton
                                size="small"
                                // Clear merchant filter quickly
                                onClick={() => setRequestMerchantFilter('')}
                                sx={{ mr: 1 }}
                              >
                                <Cancel />
                              </IconButton>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={requestStatusFilter}
                    label="Status"
                    onChange={(e) => setRequestStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {requestStatuses.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={subscriptionRequestFromDate}
                  onChange={(e) => {
                    setSubscriptionRequestFromDate(e.target.value);
                    // Clear error when date changes
                    setSubscriptionRequestDateError('');
                    // Real-time validation
                    const dateValidation = validateDateRange(e.target.value, subscriptionRequestToDate);
                    if (!dateValidation.isValid) {
                      setSubscriptionRequestDateError(dateValidation.message);
                    }
                  }}
                  error={!!subscriptionRequestDateError}
                  helperText={subscriptionRequestDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={subscriptionRequestToDate}
                  onChange={(e) => {
                    setSubscriptionRequestToDate(e.target.value);
                    // Clear error when date changes
                    setSubscriptionRequestDateError('');
                    // Real-time validation
                    const dateValidation = validateDateRange(subscriptionRequestFromDate, e.target.value);
                    if (!dateValidation.isValid) {
                      setSubscriptionRequestDateError(dateValidation.message);
                    }
                  }}
                  error={!!subscriptionRequestDateError}
                  helperText={subscriptionRequestDateError}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Disable future dates
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  variant="contained"
                  onClick={() => loadSubscriptionRequests()}
                  disabled={!!subscriptionRequestDateError}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Subscription Requests Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Request Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {request.merchant_name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.merchant_email || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{request.package_name || 'N/A'}</TableCell>
                    <TableCell>{formatCredits(request.requested_credits)}</TableCell>
                    <TableCell>₹{request.requested_amount}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.status}
                        color={getRequestStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.request_type || 'CREDIT'}
                        color={request.request_type === 'SUBSCRIPTION' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {request.status === 'PENDING' && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              onClick={() => handleSubscriptionRequestAction(request, 'approve')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleSubscriptionRequestAction(request, 'reject')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          size="small"
                          onClick={() => handleViewSubscriptionRequest(request)}
                        >
                          View
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {subscriptionRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No subscription requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {subscriptionRequestTotal} requests
            </Typography>
            <TablePagination
              component="div"
              count={subscriptionRequestTotal}
              page={subscriptionRequestPage}
              onPageChange={(e, newPage) => setSubscriptionRequestPage(newPage)}
              rowsPerPage={subscriptionRequestRowsPerPage}
              onRowsPerPageChange={(e) => {
                setSubscriptionRequestRowsPerPage(parseInt(e.target.value, 10));
                setSubscriptionRequestPage(0);
                loadSubscriptionRequests();
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Paper>
      )}

      {/* Package Dialog */}
      <Dialog open={packageDialog} onClose={() => setPackageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPackage ? 'Edit Package' : 'New Package'}
        </DialogTitle>
        <DialogContent>
          {/* Show error message inside dialog if there's an error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Package Name"
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tokens"
                type="number"
                value={packageForm.credits}
                onChange={(e) => {
                  // Only allow positive integers - prevent string input
                  const inputValue = e.target.value;
                  // Only allow digits
                  if (/^\d*$/.test(inputValue)) {
                    const value = Math.max(0, parseInt(inputValue) || 0);
                    setPackageForm({ ...packageForm, credits: value.toString() });
                  }
                }}
                onKeyPress={(e) => {
                  // Prevent non-numeric characters
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  min: 1, // Set minimum value to 1
                  step: 1, // Increment by 1 instead of 0.1
                }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Price (₹)"
                type="number"
                value={packageForm.price}
                onChange={(e) => {
                  // Only allow positive numbers - prevent string input
                  const inputValue = e.target.value;
                  // Only allow digits and decimal point
                  if (/^\d*\.?\d*$/.test(inputValue)) {
                    const value = Math.max(0, parseFloat(inputValue) || 0);
                    setPackageForm({ ...packageForm, price: value.toString() });
                  }
                }}
                onKeyPress={(e) => {
                  // Prevent non-numeric characters except decimal point
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                  // Prevent multiple decimal points
                  if (e.key === '.' && e.target.value.includes('.')) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  min: 0, // Set minimum value to 0
                  step: 0.01, // Allow decimal values for price
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={packageForm.description}
                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={packageForm.is_active}
                    onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDialog(false)}>Cancel</Button>
          <Button 
            onClick={editingPackage ? handleUpdatePackage : handleCreatePackage} 
            variant="contained"
          >
            {editingPackage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Action Dialog */}
      <Dialog open={requestDialog} onClose={() => setRequestDialog(false)}>
        <DialogTitle>
          {requestAction === 'approve' ? 'Approve Token Request' : 'Reject Token Request'}
        </DialogTitle>
        <DialogContent>
          {/* Approval warning when amount exceeds max allocation */}
          {requestAction === 'approve' && selectedRequest && selectedRequest.requested_credits > maxCreditLimit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                ⚠️ <strong>Warning:</strong> This request for {formatCredits(selectedRequest.requested_credits)} tokens exceeds the maximum allocation limit of {formatCredits(maxCreditLimit)} tokens.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Approving this request may violate system limits. Consider rejecting or asking the merchant to reduce their request.
              </Typography>
            </Alert>
          )}
          
          <TextField
            fullWidth
            label={requestAction === 'approve' ? 'Admin Notes (Optional)' : 'Rejection Reason (Required)'}
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required={requestAction === 'reject'}
            error={requestAction === 'reject' && !requestReason.trim()}
            helperText={requestAction === 'reject' && !requestReason.trim() ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRequestAction} 
            color={requestAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={requestAction === 'reject' && !requestReason.trim()}
          >
            {requestAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Request Action Dialog */}
      <Dialog open={subscriptionRequestDialog} onClose={() => setSubscriptionRequestDialog(false)}>
        <DialogTitle>
          {subscriptionRequestAction === 'approve' ? 'Approve Subscription Request' : 
           subscriptionRequestAction === 'reject' ? 'Reject Subscription Request' : 
           'View Subscription Request'}
        </DialogTitle>
        <DialogContent>
          {selectedSubscriptionRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>Request Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Merchant:</Typography>
                  <Typography variant="body1">{selectedSubscriptionRequest.merchant_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Package:</Typography>
                  <Typography variant="body1">{selectedSubscriptionRequest.package_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Tokens:</Typography>
                  <Typography variant="body1">{formatCredits(selectedSubscriptionRequest.requested_credits)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Amount:</Typography>
                  <Typography variant="body1">₹{selectedSubscriptionRequest.requested_amount}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Request Type:</Typography>
                  <Chip 
                    label={selectedSubscriptionRequest.request_type || 'CREDIT'} 
                    color={selectedSubscriptionRequest.request_type === 'SUBSCRIPTION' ? 'primary' : 'default'}
                    size="small"
                  />
                </Grid>
                {selectedSubscriptionRequest.request_reason && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Reason:</Typography>
                    <Typography variant="body1">{selectedSubscriptionRequest.request_reason}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          {subscriptionRequestAction !== 'view' && (
            <TextField
              fullWidth
              label={subscriptionRequestAction === 'approve' ? 'Admin Notes (Optional)' : 'Rejection Reason (Required)'}
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
              error={subscriptionRequestAction === 'reject' && !requestReason.trim()}
              helperText={subscriptionRequestAction === 'reject' && !requestReason.trim() ? 'Rejection reason is required' : ''}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionRequestDialog(false)}>Cancel</Button>
          {subscriptionRequestAction !== 'view' && (
            <Button 
              onClick={handleSubscriptionRequestSubmit} 
              color={subscriptionRequestAction === 'approve' ? 'success' : 'error'}
              variant="contained"
              disabled={subscriptionRequestAction === 'reject' && !requestReason.trim()}
            >
              {subscriptionRequestAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Delete />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.1rem', color: 'text.primary', mb: 2 }}>
            Are you sure you want to delete the token package{' '}
            <strong>{packageToDelete?.name}</strong>?
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            This action cannot be undone. The package will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreditSystemManagement; 